import * as React from "react"
import {
  Dialog as DialogPrimitive,
  Heading,
  HeadingContext,
  Modal,
  ModalOverlay,
  type ModalOverlayProps,
} from "react-aria-components"

import { cn } from "@/lib/utils"

/**
 * Right-side drawer primitive over react-aria-components (a Modal variant of `dialog.tsx`). Slides in from the right
 * edge — room for a multi-field form (create/edit a facility) without covering the tree on wide screens. Controlled via
 * `isOpen`/`onOpenChange`; traps focus, restores it on close, dismisses on Esc/backdrop. `children` may be a render
 * function receiving `{ close }`. Token-based styling so light + dark both work.
 */
/** Width tiers — `md` (default, a single-field form) … `xl` (a two-pane browser, e.g. the asset library). */
const DRAWER_SIZE = { md: "max-w-md", lg: "max-w-xl", xl: "max-w-3xl" } as const

/**
 * The same content as a column beside the page instead of a modal over it: the page stays usable next to it. Lives inside the
 * surrounding flex row, and lies over the page where the row runs out of width. Escape is the surrounding page's to handle.
 */
function DockedDrawer({ className, children }: { className?: string; children: React.ReactNode }) {
  const titleId = React.useId()
  const ref = React.useRef<HTMLElement>(null)
  // Nothing traps the focus here, so hand it over on open and give it back on close.
  React.useEffect(() => {
    const opener = document.activeElement
    ref.current?.focus()
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [])
  return (
    <aside
      ref={ref}
      tabIndex={-1}
      aria-labelledby={titleId}
      data-slot="drawer"
      className={cn(
        "bg-card text-card-foreground border-border absolute inset-y-0 right-0 z-20 flex w-full max-w-[26rem] shrink-0 flex-col gap-4 border-l p-4 shadow-xl outline-hidden xl:relative xl:shadow-none",
        className,
      )}
    >
      {/* Its own title context: the surrounding dialog's would otherwise rename that dialog after this panel. */}
      <HeadingContext.Provider value={{ id: titleId, level: 2 }}>{children}</HeadingContext.Provider>
    </aside>
  )
}

function Drawer({
  className,
  overlayClassName,
  children,
  isDismissable = true,
  size = "md",
  docked = false,
  ...props
}: ModalOverlayProps & {
  className?: string
  /** Merged onto the backdrop `ModalOverlay` — e.g. a `z-[80]` bump when the drawer must stack ABOVE another modal (a nested
   *  picker over a full-screen editor). Merged via `cn`, so a `z-*` here overrides the default `z-50` (tailwind-merge dedupes). */
  overlayClassName?: string
  /** Panel width tier — defaults to `md`. Use `xl` for a file browser / list surface. */
  size?: keyof typeof DRAWER_SIZE
  /** A column beside the page instead of a modal over it. Place the drawer inside the page's flex row. */
  docked?: boolean
  children: React.ReactNode | ((opts: { close: () => void }) => React.ReactNode)
}) {
  if (docked) {
    const close = () => props.onOpenChange?.(false)
    if (props.isOpen === false) return null
    return (
      <DockedDrawer className={className}>
        {typeof children === "function" ? (children as (opts: { close: () => void }) => React.ReactNode)({ close }) : children}
      </DockedDrawer>
    )
  }
  return (
    <ModalOverlay
      isDismissable={isDismissable}
      {...props}
      className={cn(
        "fixed inset-0 z-50 flex justify-end bg-black/40 duration-150 data-entering:animate-in data-entering:fade-in-0 data-exiting:animate-out data-exiting:fade-out-0",
        overlayClassName,
      )}
    >
      <Modal
        className={cn(
          "h-full w-full outline-hidden duration-150 data-entering:animate-in data-entering:slide-in-from-right data-exiting:animate-out data-exiting:slide-out-to-right",
          DRAWER_SIZE[size],
        )}
      >
        <DialogPrimitive
          data-slot="drawer"
          className={cn(
            "bg-card text-card-foreground border-border flex h-full flex-col gap-4 border-l p-6 shadow-xl outline-hidden",
            className
          )}
        >
          {children}
        </DialogPrimitive>
      </Modal>
    </ModalOverlay>
  )
}

/**
 * Drawer header row — the title (+ optional close button) with a divider beneath it. Every drawer uses this so the
 * "divider under the title" is consistent across the app. Children are laid out as `title | actions` (space-between).
 */
function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn("border-border flex items-start justify-between gap-2 border-b pb-4", className)}
      {...props}
    />
  )
}

function DrawerTitle({ className, ...props }: React.ComponentProps<typeof Heading>) {
  return <Heading slot="title" data-slot="drawer-title" className={cn("text-lg font-semibold", className)} {...props} />
}

function DrawerDescription({ className, ...props }: React.ComponentProps<"p">) {
  return <p data-slot="drawer-description" className={cn("text-muted-foreground text-sm", className)} {...props} />
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="drawer-footer" className={cn("mt-auto flex justify-end gap-2 pt-4", className)} {...props} />
}

export { Drawer, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter }
